import { Phone, Mail, MapPin, Clock, MessageCircle, Navigation, Facebook, Instagram } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { ContactForm } from "@/components/ContactForm";
import { BUSINESS, waLink, SOCIALS } from "@/data/site";

const InfoRow = ({ icon: Icon, label, children, testid }) => (
  <div data-testid={testid} className="flex items-start gap-4 rounded-2xl border border-border bg-card shadow-sm p-5">
    <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/15 text-primary shrink-0">
      <Icon className="w-5 h-5" />
    </span>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{label}</p>
      <div className="mt-1 text-sm md:text-base text-foreground leading-relaxed break-words">{children}</div>
    </div>
  </div>
);

export const ContactSection = ({ withHeading = true }) => {
  const { address } = BUSINESS;
  const mapsDir = "https://www.google.com/maps/dir/?api=1&destination=" +
    encodeURIComponent("Wahlekarwadi, Pimpri Chinchwad, Pune, Maharashtra 411033");

  return (
    <section id="contact" className="relative py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {withHeading && (
          <div className="max-w-2xl mb-14">
            <Reveal as="p" className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Contact</Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 text-4xl md:text-5xl font-heading font-bold tracking-tight">
                Let's build something that grows your business
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-4 text-muted-foreground text-lg">
              Tell us about your project and we'll get back within 24 hours with a free custom mockup and a tailored proposal.
            </Reveal>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <Reveal className="grid gap-4 content-start">
            <InfoRow icon={Phone} label="Call / WhatsApp" testid="info-phone">
              <a href={`tel:${BUSINESS.phoneRaw}`} className="hover:text-primary transition-colors">{BUSINESS.phoneDisplay}</a>
            </InfoRow>
            <InfoRow icon={Mail} label="Email" testid="info-email">
              <a href={`mailto:${BUSINESS.email}`} className="hover:text-primary transition-colors break-all">{BUSINESS.email}</a>
            </InfoRow>
            <InfoRow icon={MapPin} label="Business Address" testid="info-address">
              {address.line1}<br />{address.line2}<br />{address.line3}<br />{address.line4}
            </InfoRow>
            <InfoRow icon={Clock} label="Business Hours" testid="info-hours">
              Monday – Saturday<br />9:00 AM – 7:00 PM (IST)
            </InfoRow>
            <div className="flex flex-wrap gap-3 pt-1">
              <a href={waLink(`Hi ${BUSINESS.name}, I'd like a free website mockup.`)} target="_blank" rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center gap-2 rounded-full bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-5 py-3 transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
              <a href={mapsDir} target="_blank" rel="noopener noreferrer" data-testid="contact-directions-btn"
                className="inline-flex items-center gap-2 rounded-full border border-border hover:border-primary hover:text-primary text-sm font-semibold px-5 py-3 transition-colors">
                <Navigation className="w-4 h-4" /> Get Directions
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="rounded-3xl border border-border bg-card p-6 md:p-8 shadow-sm">
            <ContactForm />
          </Reveal>
        </div>

        <Reveal className="mt-10 overflow-hidden rounded-3xl border border-border">
          <iframe
            title="RedKey Digital location"
            data-testid="contact-map"
            src={BUSINESS.mapEmbed}
            width="100%"
            height="380"
            style={{ border: 0, filter: "grayscale(0.15)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Reveal>

        <Reveal className="mt-10 rounded-3xl border border-border bg-card shadow-sm p-8 md:p-10 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Stay connected</p>
          <h3 className="mt-3 text-2xl md:text-3xl font-heading font-bold tracking-tight">Follow RedKey Digital</h3>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            Follow us for website design ideas, AI website demos, digital marketing tips, and business growth strategies.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook"
              className="inline-flex items-center gap-2 rounded-full bg-[#1877F2] hover:bg-[#0f66d6] text-white font-semibold px-6 py-3 transition-colors">
              <Facebook className="w-5 h-5" /> Facebook
            </a>
            <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram"
              className="inline-flex items-center gap-2 rounded-full text-white font-semibold px-6 py-3 transition-opacity hover:opacity-90"
              style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }}>
              <Instagram className="w-5 h-5" /> Instagram
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
